module Client.IllyAdmin.Grid

open Elmish
open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client
open Illy.SafeStack.Client.jQueryUI
open Client.IllyAdmin.Actions
open Client.IllyAdmin.Actions.Types
open SafeStack.Client

[<RequireQualifiedAccess>]
type RenderMode =
    | Overlay
    | Page

module Action =
    type Action =
        | ViewConfigMetadata of ConfigMetadata.Model
        | ViewHilos of Hilos.Model
        | ViewLoggerMetadata of Loggers.Grid.Model
        | ViewTokenRevocationList of TokenRevocationList.Model

    type Model =
        {
            Key: AdminActionKey
            Action: Action
            RenderIn: RenderMode
        }

    type Msg =
        | ViewConfigMetadataMsg of ConfigMetadata.Msg
        | ViewHilosMsg of Hilos.Msg
        | ViewLoggerMetadataMsg of Loggers.Grid.Msg
        | ViewTokenRevocationListMsg of TokenRevocationList.Msg

    let metaFor =
        function
        | AdminActionKey.BrowseConfigMetadata -> Actions.ConfigMetadata.metaData
        | AdminActionKey.BrowseHilos -> Actions.Hilos.metaData
        | AdminActionKey.BrowseLoggers -> Actions.Loggers.Grid.metaData
        | AdminActionKey.ViewRevocations -> Actions.TokenRevocationList.metaData

    let init key =
        let model action mode = { Key = key; Action = action; RenderIn = mode }
        match key with
        | AdminActionKey.BrowseConfigMetadata ->
            let m,c = ConfigMetadata.init ()
            model (Action.ViewConfigMetadata m) RenderMode.Page, Cmd.map ViewConfigMetadataMsg c
        | AdminActionKey.BrowseHilos ->
            let m,c = Hilos.init ()
            model (Action.ViewHilos m) RenderMode.Page, Cmd.map ViewHilosMsg c
        | AdminActionKey.BrowseLoggers ->
            let m, c = Loggers.Grid.init ()
            model (Action.ViewLoggerMetadata m) RenderMode.Page, Cmd.map ViewLoggerMetadataMsg c
        | AdminActionKey.ViewRevocations ->
            let m,c = TokenRevocationList.init ()
            model (Action.ViewTokenRevocationList m) RenderMode.Page, Cmd.map ViewTokenRevocationListMsg c

    module Dispatch =
        let viewConfigMetadataMsg model msg =
            match model.Action with
            | Action.ViewConfigMetadata o ->
                let u, eff = ConfigMetadata.update msg o
                { model with Action = ViewConfigMetadata u }, SideEffect.map ViewConfigMetadataMsg eff
            | _ -> model, SideEffect.none

        let viewHilosMsg model msg =
            match model.Action with
            | Action.ViewHilos o ->
                let u, eff = Hilos.update msg o
                { model with Action = ViewHilos u }, SideEffect.map ViewHilosMsg eff
            | _ -> model, SideEffect.none

        let viewLoggerMetadataMsg model msg =
            match model.Action with
            | Action.ViewLoggerMetadata o ->
                let u, eff = Loggers.Grid.update msg o
                { model with Action = ViewLoggerMetadata u }, SideEffect.map ViewLoggerMetadataMsg eff
            | _ -> model, SideEffect.none

        let viewTokenRevocationListMsg model msg =
            match model.Action with
            | Action.ViewTokenRevocationList o ->
                let u, eff = TokenRevocationList.update msg o
                { model with Action = ViewTokenRevocationList u }, SideEffect.map ViewTokenRevocationListMsg eff
            | _ -> model, SideEffect.none

    let update msg model =
        match msg with
        | Msg.ViewConfigMetadataMsg msg -> Dispatch.viewConfigMetadataMsg model msg
        | Msg.ViewHilosMsg msg -> Dispatch.viewHilosMsg model msg
        | Msg.ViewLoggerMetadataMsg msg -> Dispatch.viewLoggerMetadataMsg model msg
        | Msg.ViewTokenRevocationListMsg msg -> Dispatch.viewTokenRevocationListMsg model msg

    let view model dispatch =
        match model.Action with
        | ViewConfigMetadata l -> ConfigMetadata.view l (ViewConfigMetadataMsg >> dispatch)
        | ViewHilos l -> Hilos.view l (ViewHilosMsg >> dispatch)
        | ViewLoggerMetadata l -> Loggers.Grid.view l (ViewLoggerMetadataMsg >> dispatch)
        | ViewTokenRevocationList l -> TokenRevocationList.view l (ViewTokenRevocationListMsg >> dispatch)


type Msg =
    | GridMsg of Grid.Msg<unit>
    | OpenActionMsg of Action.Msg
    | OpenAction of AdminActionKey
    | EndNotification
    | CloseAction

type Events =
    | NoEvent

type Model =
    {
        Grid:  Grid.Model<AdminActionMeta, unit>
        OpenAction: Action.Model option
    }

module Init =
    let grid actions =
        Grid.init "admin action" "admin actions" "There are no admin actions that match the selected criteria" []
        |> Grid.updateData actions actions.Length

let allAdminActions =
    AdminActionKey.options
    |> List.map Action.metaFor
    |> List.sortBy (fun x -> ActionGroup.toString x.Group, x.Title)

let init() =
    {
        Grid = Init.grid allAdminActions
        OpenAction = None
    }, Cmd.none

module Dispatch =
    let gridMsg model msg =
        let m, eff = Grid.update msg model.Grid
        {model with Grid = m},
        match eff with
        | SideEffect.Cmd c -> Cmd.map GridMsg c |> SideEffect.Cmd
        | SideEffect.Event (Grid.GridSideEffect.ButtonClicked ()) -> SideEffect.none

    let openActionMsg model msg =
        match model.OpenAction with
        | None -> model, SideEffect.none
        | Some a ->
            let m, eff = Action.update msg a
            match eff with
            | SideEffect.Cmd c -> 
                {model with OpenAction = Some m}, Cmd.map OpenActionMsg c |> SideEffect.Cmd
            | SideEffect.Event ev ->
                match ev with
                | ActionEvent.AppError err -> {model with OpenAction = Some m}, SideEffect.Event err
                | ActionEvent.BackToGrid notification ->
                    {model with OpenAction = None},
                    Cmd.batch [
                        match notification with
                        | None -> ()
                        | Some n -> yield Notification.success n EndNotification
                    ] |> SideEffect.Cmd

let update msg model =
    match msg with
    | Msg.OpenAction key ->
        let m, c = Action.init key
        { model with OpenAction = Some m }, c |> Cmd.map OpenActionMsg |> SideEffect.Cmd
    | Msg.GridMsg gMsg ->
        Dispatch.gridMsg model gMsg
    | Msg.OpenActionMsg aMsg ->
        Dispatch.openActionMsg model aMsg
    | CloseAction -> { model with OpenAction = None }, SideEffect.none
    | EndNotification -> model, SideEffect.none

module Render =
    let grid grid dispatch =
        Grid.view
            grid
            [
                None ===> fun x -> [
                    a [ OnClick (fun _ -> dispatch (OpenAction x.Key)) ] [ str "Open" ]
                ]
                Some [ str "Group"] ===> fun x -> [ str (ActionGroup.toString x.Group) ]
                Some [ str "Action"] ===> fun x -> [ str x.Title ]
                Some [ str "Description"] ===> fun x -> [ str x.Description ]
            ]
            false
            (GridMsg >> dispatch)

let view model dispatch =
    div [ Class "home_theme illy_admin_theme" ] [
        div [
                Style [ ZIndex 500 ]
        ] [
            yield div [ Id "adminBar" ] [
                match model.OpenAction with
                | None ->
                    yield h2 [] [ str "Illy Admin Actions" ]
                    yield! Render.grid model.Grid dispatch
                | Some am ->
                    let meta = Action.metaFor am.Key
                    match am.RenderIn with
                    | RenderMode.Overlay ->
                        yield! Render.grid model.Grid dispatch
                        yield! jQueryUI.modal meta.Title 10 CloseAction dispatch (Some 1000) [
                            yield! Action.view am (OpenActionMsg >> dispatch)
                        ]
                    | RenderMode.Page ->
                        yield div [
                            ClassName "gridControls"
                        ] [
                            h2 [] [ str meta.Title ]
                            a [
                                OnClick (fun _ -> dispatch CloseAction)
                            ] [
                                FontAwesome.regular "external-link" [ FontAwesome.Modifier.Fixed ] []
                                str "Back to grid"
                            ]
                        ]
                        yield! Action.view am (OpenActionMsg >> dispatch)
            ]
        ]
    ]